/* eslint-disable prefer-promise-reject-errors */
import axios from 'axios';
import { List, fromJS } from 'immutable';
import { forEach, join, find, isEmpty, isNumber, isString, map, orderBy, toUpper, isNull } from 'lodash';

import config from 'commons-vehicle/config/index.js';
import { LocaleService, ConfigsService, HTTPService } from '@sm360/widget-commons-core-services';
import { AccountService, CookieService, COOKIES } from '@sm360/widget-commons-general';

import { ITEM_SOURCE } from 'commons-vehicle/Quote/constants/modelTrimItemCategories.js';
import VEHICLE from 'commons-vehicle/constants/vehicle.js';
import * as dataTypes from 'commons-vehicle/constants/dataTypes.js';
import * as DataOrigins from 'commons-vehicle/constants/dataOrigins.js';
import {
    resetModelYearFromOriginData,
    setSelectedOptionsPackage,
} from 'commons-vehicle/ModelYear/services/ModelYearService/ModelYearService.js';

import { resetQuoteFromShowroomLoad } from 'commons-vehicle/Quote/services/QuoteService/QuoteService.js';
import {
    resetQuoteItemCategoryFromCategory,
    resetQuoteItemCategoriesFromQuoteProtections,
} from 'commons-vehicle/Quote/services/QuoteItemCategoryService/QuoteItemCategoryService.js';
import QuoteModel from 'commons-vehicle/Quote/models/QuoteModel.js';
import QuoteModelV25 from 'commons-vehicle/Quote/models/QuoteModelV25.js';
import QuoteItemCategoryModel from 'commons-vehicle/Quote/models/QuoteItemCategoryModel.js';

import {
    resetPurchaseMethodsFromOriginData,
    resetPurchaseMethodsOptionsFromQuoteItems,
} from 'commons-vehicle/PurchaseMethods/services/PurchaseMethodsService/PurchaseMethodsService.js';

import { setVehiclePurchaseMethods } from 'commons-vehicle/Vehicle/services/VehicleService/VehicleService.js';

import ModelYearModel from 'commons-vehicle/ModelYear/models/ModelYearModel.js';
import PurchaseMethodsModel from 'commons-vehicle/PurchaseMethods/models/PurchaseMethodsModel.js';

import ShowroomAPIService from 'commons-vehicle/services/ShowroomService/ShowroomAPIService.js';
import DisclaimerAPIService from 'commons-vehicle/PurchaseMethods/services/PurchaseMethodsAPIService/DisclaimerAPIService.js';

import { DATA_LEVEL_7 } from 'commons-vehicle/constants/showroomApi';

/**
 * Util to uppercase the given value and encode the result to be URI compliant
 * @param {String} value String to modify
 * @returns {String} value or empty string if given param is not a string
 */
function upperEncodedString(value) {
    if (isString(value)) {
        return toUpper(encodeURIComponent(value));
    }
    return '';
}

function resetPurchaseMethodsDiscount(purchaseMethods, sellingPrice, discount) {
    let newCashPurchase = purchaseMethods.cashPurchase;
    newCashPurchase = newCashPurchase.set('retail', sellingPrice + discount).set('discounts', discount);

    const newFinanceTerms = map(purchaseMethods.finance.terms, (term) => {
        return term.set('discounts', discount);
    });
    const newFinance = purchaseMethods.finance.set('terms', newFinanceTerms);

    const newLeaseTerms = map(purchaseMethods.lease.terms, (term) => {
        return term.set('discounts', discount);
    });
    const newLease = purchaseMethods.lease.set('terms', newLeaseTerms);

    return purchaseMethods
        .set('cashPurchase', newCashPurchase)
        .set('finance', newFinance)
        .set('lease', newLease);
}

// TODO: #complete - documentation & tests
class ShowroomService {
    constructor() {
        this.basePath = `${config.endpoint.showroom}/service/v1`;
    }

    getModelYear(callType, year, make, model, bodyStyleCode, modelTrimCode, locale) {
        const urlMakeModelSection =
            `make=${upperEncodedString(make)}&model=${upperEncodedString(model)}` +
            `&bodyStyleCode=${upperEncodedString(bodyStyleCode)}`;

        const modelString =
            callType === DataOrigins.MODEL_YEARS
                ? `${urlMakeModelSection}&subGrouping=year&datalevel=768`
                : `year=${year}&${urlMakeModelSection}&datalevel=256`;

        const organizationId = ConfigsService.getOrganizationId();
        const organizationUnitId = ConfigsService.getOrganizationUnitId();
        const location = ConfigsService.getOrganizationUnitLocation();
        const context =
            `location=${location}` +
            `&language=${locale}` +
            `&context=${organizationId}.${organizationUnitId}` +
            `&${modelString}` +
            '&compact=false&userName=public&password=public';

        return new Promise((resolve) => {
            switch (callType) {
                case DataOrigins.MODELS_COLLECTION:
                    axios.get(`${this.basePath}/view/1?${context}`).then((result) => {
                        const vehicles = resetModelYearFromOriginData(callType, result.data.view, locale);
                        resolve(vehicles[0].purchaseMethods);
                    });
                    break;

                case DataOrigins.MODEL_YEARS:
                    axios.get(`${this.basePath}/view/1.1?${context}`).then((result) => {
                        resolve(resetModelYearFromOriginData(callType, result.data.view, locale));
                    });
                    break;

                case DataOrigins.MODEL_YEAR_POWERS_TRIMS:
                    axios.get(`${this.basePath}/view/2.1?unfiltered=true&${context}`).then((result) => {
                        resolve(resetModelYearFromOriginData(callType, result.data.view, locale));
                    });
                    break;

                default:
                    resolve({
                        modelYear: new ModelYearModel(),
                        purchaseMethods: new PurchaseMethodsModel(),
                    });
            }
        });
    }

    getModels(callType, makes, locale) {
        const makesQuery = join(makes, '|');
        const modelString = `make=${makesQuery}&datalevel=256`;

        const organizationId = ConfigsService.getOrganizationId();
        const organizationUnitId = ConfigsService.getOrganizationUnitId();
        const location = ConfigsService.getOrganizationUnitLocation();
        const formattedLocale = locale === 'fr_CA' ? 'fr' : 'en';
        const context =
            `location=${location}` +
            `&language=${formattedLocale}` +
            `&context=${organizationId}.${organizationUnitId}` +
            `&${modelString}` +
            '&compact=false&userName=public&password=public';

        return DisclaimerAPIService.getGenericDisclaimers().then((disclaimers) => {
            switch (callType) {
                case DataOrigins.MODELS_COLLECTION:
                    return axios
                        .get(`${this.basePath}/view/1?${context}`)
                        .then((result) =>
                            fromJS(resetModelYearFromOriginData(callType, result.data.view, locale, disclaimers))
                        );

                default:
                    return Promise.resolve(List([]));
            }
        });
    }

    validateSelectedOptions(callType, year, make, configuration, locale, optionSelectionCode, selectionResolutions) {
        const organizationUnitLocation = ConfigsService.getOrganizationUnitLocation();
        const organizationId = ConfigsService.getOrganizationId();
        const organizationUnitId = ConfigsService.getOrganizationUnitId();
        const parameters =
            `location=${organizationUnitLocation}` +
            `&language=${locale}` +
            '&includeNonManufacturerItems=true&datalevel=65535&userName=public&password=public' +
            `&context=${organizationId}.${organizationUnitId}`;

        let selectedItems = configuration.getValidationSelectedOptions();
        if (selectionResolutions) {
            selectedItems = selectionResolutions.getResolutionActionOptions(selectedItems);
        }

        return new Promise((resolve, reject) => {
            switch (callType) {
                case DataOrigins.OPTIONS_VALIDATION:
                    axios
                        .post(`${this.basePath}/builder?${parameters}`, {
                            year,
                            make,
                            modelTrimCode: configuration.get('modelTrimCode'),
                            configurationID: configuration.get('id'),
                            purchaseMethod: configuration.get('purchaseMethods').getSelectedPurchaseMethodValue(),
                            term: configuration.get('purchaseMethods').getSelectedFinanceTermValue(),
                            items: selectedItems,
                        })
                        .then((result) => {
                            if (result.data.resolutions) {
                                reject({
                                    optionSelectionCode,
                                    errors: result.data.errors,
                                    resolutions: result.data.resolutions,
                                });
                            }
                            resolve(result.data);
                        });
                    break;

                default:
                    resolve([]);
            }
        });
    }

    getVehicleId(vehicle) {
        const {
            vehicleConditionType,
            year,
            make,
            model,
            bodyStyleCode,
            driveTrainCode,
            odometer,
            sellingPrice,
        } = vehicle;

        return (
            `${vehicleConditionType}|${year}|${make}|${model}|` +
            `${bodyStyleCode}|${driveTrainCode}|${isNull(odometer) ? 0 : odometer}|${sellingPrice}`
        );
    }

    getVehiclePayments(vehicle, oldPurchaseMethods, quote) {
        const organizationId = ConfigsService.getOrganizationId();
        const organizationUnitId = ConfigsService.getOrganizationUnitId();
        const location = ConfigsService.getOrganizationUnitLocation();
        const context =
            `location=${location}` +
            `&language=${LocaleService.getLocale()}` +
            `&context=${organizationId}.${organizationUnitId}` +
            '&datalevel=65535&userName=public&password=public';

        const {
            year,
            make,
            model,
            bodyStyleCode,
            driveTrainCode,
            odometer,
            vehicleConditionType,
            sellingPrice,
            discount,
            vin,
            showroomVehicleId,
        } = vehicle;

        const isUsed =
            vehicleConditionType === VEHICLE.CONDITIONS.USED || vehicleConditionType === VEHICLE.CONDITIONS.CERTIFIED;
        const paymentParameters = {
            id: showroomVehicleId,
            showroomVehicleId,
            vin,
            year,
            make,
            model,
            bodyStyleCode,
            driveTrainCode,
            odometer,
            vehicleCondition: vehicleConditionType,
            amounts: isUsed ? { retail: sellingPrice } : null,
        };

        return DisclaimerAPIService.getGenericDisclaimers().then((disclaimers) => {
            return axios.post(`${this.basePath}/vehiclePayments?${context}`, paymentParameters).then((result) => {
                let purchaseMethods = resetPurchaseMethodsFromOriginData(
                    DataOrigins.VEHICLE_PAYMENTS,
                    result.data,
                    oldPurchaseMethods,
                    disclaimers
                );
                if (isUsed) {
                    // Check if there is quote id is available, get the showroom items from quote and check for protection available
                    // If no quote id available, set the default quote item from vehicle
                    if (quote?.id) {
                        const purchaseMethodItems = [];
                        purchaseMethodItems.push(vehicle.getDefaultVehicleQuoteItem());
                        const showroomtItems = quote.showroomItems;
                        // Get the protection item
                        const protectionDataTypeItem = showroomtItems.find(
                            (item) => item.dataType === dataTypes.DATA_TYPES.PROTECTION.DATA_TYPE_ID
                        );
                        // If no protection item available from quote, use the default vehicle quote item
                        if (protectionDataTypeItem) purchaseMethodItems.push(protectionDataTypeItem);

                        purchaseMethods = resetPurchaseMethodsOptionsFromQuoteItems(
                            purchaseMethods,
                            purchaseMethodItems
                        );
                    } else {
                        purchaseMethods = resetPurchaseMethodsOptionsFromQuoteItems(purchaseMethods, [
                            vehicle.getDefaultVehicleQuoteItem(),
                        ]);
                    }
                    if (discount) {
                        purchaseMethods = resetPurchaseMethodsDiscount(purchaseMethods, sellingPrice, discount);
                    }
                }
                const vehicleWithPurchaseMethods = setVehiclePurchaseMethods(vehicle, purchaseMethods);

                return DisclaimerAPIService.getVehiclesDisclaimers([vehicleWithPurchaseMethods]).then(
                    (vehiclesWithDisclaimers) => Promise.resolve(vehiclesWithDisclaimers[0])
                );
            });
        });
    }

    getVehiclesPayments(vehicles, purchaseMethod, termMinimum, termMaximum) {
        const organizationId = ConfigsService.getOrganizationId();
        const organizationUnitId = ConfigsService.getOrganizationUnitId();
        const location = ConfigsService.getOrganizationUnitLocation();
        const language = LocaleService.getLocale();
        const context = `language=${language}&location=${location}&context=${organizationId}.${organizationUnitId}&userName=public&password=public`;

        const paymentParameters = {
            termMinimum,
            termMaximum,
            purchaseMethod,
            vehicles: vehicles.map((vehicle) => vehicle.getPaymentCalculatorVehicleParameters()),
        };

        return DisclaimerAPIService.getGenericDisclaimers().then((disclaimers) => {
            return axios
                .post(`${this.basePath}/sm360core.paymentCalculator?${context}`, paymentParameters)
                .then((result) => {
                    const newVehicles = [];
                    forEach(vehicles, (vehicle) => {
                        const matchVehicle = find(
                            result.data.vehicles,
                            (resultVehicle) => this.getVehicleId(resultVehicle) === this.getVehicleId(vehicle)
                        );

                        if (matchVehicle) {
                            result.data.vehicle = matchVehicle;
                            let newPurchaseMethods = resetPurchaseMethodsFromOriginData(
                                DataOrigins.PAYMENT_CALCULATOR,
                                result.data
                            );
                            newPurchaseMethods = newPurchaseMethods.set('disclaimers', disclaimers);

                            if (!vehicle.getIsVehicleNew() && vehicle.discount) {
                                newPurchaseMethods = resetPurchaseMethodsDiscount(
                                    newPurchaseMethods,
                                    vehicle.sellingPrice,
                                    vehicle.discount
                                );
                            }
                            const newVehicle = setVehiclePurchaseMethods(vehicle, newPurchaseMethods);
                            newVehicles.push(newVehicle);
                        } else {
                            newVehicles.push(vehicle);
                        }
                    });

                    return DisclaimerAPIService.getVehiclesDisclaimers(newVehicles);
                });
        });
    }

    loadQuote(quoteId, dataLevel = DATA_LEVEL_7, loadQuoteAsPublic = false) {
        return DisclaimerAPIService.getGenericDisclaimers().then((disclaimers) => {
            return ShowroomAPIService.getPublicToken().then((response) => {
                const userCookie = CookieService.getToken(COOKIES.ACCOUNT.AUTHENTICATE_TOKEN);
                const organizationId = ConfigsService.getOrganizationId();
                const organizationUnitId = ConfigsService.getOrganizationUnitId();
                const location = ConfigsService.getOrganizationUnitLocation();
                const language = LocaleService.getLocale();
                const context =
                    `datalevel=${dataLevel}&source=4&dataTypes=3&context=${organizationId}.${organizationUnitId}` +
                    `&location=${location}&language=${language}&refresh=1`;

                return axios
                    .get(`${this.basePath}/Quote/${quoteId}?${context}`, {
                        headers: {
                            'x-jwt': loadQuoteAsPublic ? response.data : userCookie || response.data,
                        },
                    })
                    .then((result) => {
                        const quote = resetQuoteFromShowroomLoad(new QuoteModel(), result.data, disclaimers);

                        return Promise.resolve(quote);
                    });
            });
        });
    }

    loadQuotesV25() {
        const token = CookieService.getToken(COOKIES.ACCOUNT.AUTHENTICATE_TOKEN);
        const accountId = AccountService.getAccountId(token);
        return this.searchQuotesV25(token, accountId);
    }

    loadQuotes() {
        const token = CookieService.getToken(COOKIES.ACCOUNT.AUTHENTICATE_TOKEN);
        return this.searchQuotes(token);
    }

    loadQuotesByLeadId(leadId) {
        if (!isNumber(leadId)) {
            throw TypeError('param [leadId] must be a number');
        }
        const token = ConfigsService.getDeskingToken();
        return this.searchQuotes(token, { leadId });
    }

    searchQuoteV25(quoteId) {
        // eslint-disable-next-line no-unused-vars
        return DisclaimerAPIService.getGenericDisclaimers().then((disclaimers) => {
            return ShowroomAPIService.getPublicToken().then((response) => {
                const userCookie = CookieService.getToken(COOKIES.ACCOUNT.AUTHENTICATE_TOKEN);
                const organizationId = ConfigsService.getOrganizationId();
                const organizationUnitId = ConfigsService.getOrganizationUnitId();
                const location = ConfigsService.getOrganizationUnitLocation();
                const language = LocaleService.getLocale();
                const context =
                    `datalevel=7&source=4&dataTypes=3&context=${organizationId}.${organizationUnitId}` +
                    `&location=${location}&language=${language}&module=2`;
                const headers = {
                    'x-jwt': userCookie || response.data,
                };
                return axios.get(`${this.basePath}/quote/summary/${quoteId}?${context}`, { headers }).then((result) => {
                    const quote = new QuoteModelV25(result.data);
                    // we need to upgrade all of this to match quote model
                    return Promise.resolve(quote);
                });
            });
        });
    }

    searchQuotesV25(token, accountId) {
        const organizationId = ConfigsService.getOrganizationId();
        const organizationUnitId = ConfigsService.getOrganizationUnitId();
        const location = ConfigsService.getOrganizationUnitLocation();
        const language = LocaleService.getLocale();
        const context = `${
            location ? `location=${location}&` : ''
        }language=${language}&orgId=${organizationId}&orgUnitId=${organizationUnitId}&ignoreDeleted=true&module=2`;

        const url = `${this.basePath}/quote/summaries/${accountId}?${context}`;

        return axios
            .get(url, {
                headers: {
                    'x-jwt': token,
                },
            })
            .then((result) => {
                const quotes = [];
                if (isEmpty(result.data)) {
                    return Promise.resolve(quotes);
                }

                forEach(result.data, (showroomQuote) => {
                    quotes.push(new QuoteModelV25(showroomQuote));
                });

                quotes.sort((a, b) => {
                    return b.modifiedDate - a.modifiedDate;
                });

                return Promise.resolve(quotes);
            });
    }

    searchQuotes(token, searchParams) {
        const organizationId = ConfigsService.getOrganizationId();
        const organizationUnitId = ConfigsService.getOrganizationUnitId();
        const location = ConfigsService.getOrganizationUnitLocation();
        const language = LocaleService.getLocale();
        const context = `${
            location ? `location=${location}&` : ''
        }language=${language}&context=${organizationId}.${organizationUnitId}`;

        let searchParamsString = '';
        if (searchParams) {
            searchParamsString = Object.entries(searchParams)
                .map(([key, val]) => `${key}=${val}`)
                .join('&');
        }

        const url =
            `${this.basePath}/Quotes?${context}&offset=0&limit=10&quoteDetail=true&datalevel=3` +
            `${searchParamsString ? `&${searchParamsString}` : ''}`;

        return axios
            .get(url, {
                headers: {
                    'x-jwt': token,
                },
            })
            .then((result) => {
                const quotes = [];
                if (isEmpty(result.data)) {
                    return Promise.resolve(quotes);
                }

                forEach(result.data, (showroomQuote) => {
                    quotes.push(resetQuoteFromShowroomLoad(new QuoteModel(), showroomQuote));
                });
                return Promise.resolve(quotes);
            });
    }

    updateQuoteV25(UpdateQuoteModel) {
        const userCookie = CookieService.getToken(COOKIES.ACCOUNT.AUTHENTICATE_TOKEN);
        const organizationId = ConfigsService.getOrganizationId();
        const organizationUnitId = ConfigsService.getOrganizationUnitId();
        const location = ConfigsService.getOrganizationUnitLocation();
        const language = LocaleService.getLocale();
        const params = `orgId=${organizationId}&orgUnitId=${organizationUnitId}&location=${location}&language=${language}`;
        const headers = { 'x-jwt': userCookie };

        return axios
            .put(`${this.basePath}/quote/desking/${UpdateQuoteModel.id}?${params}`, UpdateQuoteModel, { headers })
            .then((result) => {
                const quoteId = result.data.id;
                return Promise.resolve(quoteId);
            });
    }

    saveQuoteV25(saveNewQuoteDetails, asPublic) {
        const organizationId = ConfigsService.getOrganizationId();
        const organizationUnitId = ConfigsService.getOrganizationUnitId();
        const location = ConfigsService.getOrganizationUnitLocation();
        const language = LocaleService.getLocale();

        const context =
            `orgId=${organizationId}&orgUnitId=${organizationUnitId}&location=${location}` +
            `&lang=${language}&priceIncreaseRollCount=0&module=2`;

        let url =
            saveNewQuoteDetails.showroomVehicleId && saveNewQuoteDetails.vehicleCondition.indexOf('new') === 0
                ? `${this.basePath}/quote/vehicle/listingNew/${saveNewQuoteDetails.showroomVehicleId}?${context}`
                : `${this.basePath}/quote/vehicle/listingUsed?${context}`;

        if (!saveNewQuoteDetails.vehicleId) {
            url = `${this.basePath}/quote/vehicle/catalog?${context}`;
        }

        return new Promise((resolve) => {
            if (asPublic) {
                return ShowroomAPIService.getPublicToken().then((response) => resolve(response.data));
            }
            return resolve(CookieService.getToken(COOKIES.ACCOUNT.AUTHENTICATE_TOKEN));
        }).then((token) => {
            return axios
                .put(url, saveNewQuoteDetails, {
                    headers: {
                        'x-jwt': token,
                    },
                })
                .then((result) => {
                    const quoteId = result.data.id;
                    return Promise.resolve(quoteId);
                });
        });
    }

    saveQuote(quote, validateOnly) {
        return !AccountService.isAuthenticated()
            ? this.saveQuoteToPublic(quote, validateOnly)
            : this.saveQuoteToUser(quote, validateOnly);
    }

    saveQuoteToUser(quote, validateOnly = false) {
        const quoteContext = `createWriteToken=true${validateOnly ? '&validateOnly=1' : ''}`;
        const userCookie = CookieService.getToken(COOKIES.ACCOUNT.AUTHENTICATE_TOKEN);

        return ShowroomAPIService.saveQuote(quote, userCookie, quoteContext);
    }

    saveQuoteToPublic(quote, validateOnly = false) {
        const quoteContext = validateOnly ? 'validateOnly=1' : !quote.securityToken && 'createWriteToken=true';

        return ShowroomAPIService.getPublicToken().then((response) => {
            return ShowroomAPIService.saveQuote(quote, response.data, quoteContext);
        });
    }

    deleteQuote(quote) {
        if (!quote) {
            return Promise.reject(HTTPService.formatRejectResponse('missing parameters'));
        }
        return this.saveQuote(quote, false);
    }

    deleteQuoteV25(quote) {
        if (!quote) {
            return Promise.reject(HTTPService.formatRejectResponse('missing parameters'));
        }

        const userCookie = CookieService.getToken(COOKIES.ACCOUNT.AUTHENTICATE_TOKEN);

        if (!quote.leadId) {
            // maybe was not updated yet so let's get it again
            return this.loadQuotesV25().then((accountQuotes) => {
                const quoteToDelete = accountQuotes.find((accountQuote) => quote.id === accountQuote.id);
                return ShowroomAPIService.deleteQuote(quoteToDelete, userCookie);
            });
        }

        return ShowroomAPIService.deleteQuote(quote, userCookie);
    }

    getIdentificationTypes() {
        const locale = LocaleService.getLocale();
        return axios
            .get(`${this.basePath}/IdentificationTypes?language=${locale}&userName=public&password=public`)
            .then((result) => {
                return Promise.resolve(result.data);
            });
    }

    loadQuoteProtections(quoteId) {
        const token = CookieService.getToken(COOKIES.ACCOUNT.AUTHENTICATE_TOKEN);
        const organizationId = ConfigsService.getOrganizationId();
        const organizationUnitId = ConfigsService.getOrganizationUnitId();
        const locale = LocaleService.getLocale();
        const source = `${ITEM_SOURCE.DEALER}|${ITEM_SOURCE.SUPPLIER}`;
        const context = `source=${source}&context=${organizationId}.${organizationUnitId}&language=${locale}`;

        return this.getIdentificationTypes().then((identificationTypes) => {
            let protectionCategories = [];
            if (isEmpty(identificationTypes) || isEmpty(identificationTypes.identificationDataTypes)) {
                return Promise.reject(HTTPService.formatRejectResponse('no protections categories'));
            }
            const protectionDataType = find(
                identificationTypes.identificationDataTypes,
                (resultDataType) => resultDataType.dataType === dataTypes.DATA_TYPES.PROTECTION.DATA_TYPE_ID
            );
            if (!protectionDataType) {
                return Promise.reject(HTTPService.formatRejectResponse('no protections categories'));
            }

            protectionCategories = orderBy(
                map(protectionDataType.categories, (category) => {
                    return resetQuoteItemCategoryFromCategory(
                        new QuoteItemCategoryModel(),
                        dataTypes.DATA_TYPES.PROTECTION.DATA_TYPE_ID,
                        category
                    );
                }),
                ['order']
            );

            return axios
                .get(`${this.basePath}/QuoteItems/${quoteId}?${context}`, {
                    headers: {
                        'x-jwt': token,
                    },
                })
                .then((result) => {
                    if (isEmpty(result.data)) {
                        return Promise.resolve([]);
                    }
                    return Promise.resolve(
                        resetQuoteItemCategoriesFromQuoteProtections(protectionCategories, result.data)
                    );
                });
        });
    }

    loadQuoteItems(quoteId) {
        const token = CookieService.getToken(COOKIES.ACCOUNT.AUTHENTICATE_TOKEN);
        const organizationId = ConfigsService.getOrganizationId();
        const organizationUnitId = ConfigsService.getOrganizationUnitId();
        const locale = LocaleService.getLocale();
        const source = `${ITEM_SOURCE.DEALER}|${ITEM_SOURCE.SUPPLIER}`;
        const context = `source=${source}&context=${organizationId}.${organizationUnitId}&language=${locale}`;
        return axios
            .get(`${this.basePath}/QuoteItems/${quoteId}?${context}`, {
                headers: {
                    'x-jwt': token,
                },
            })
            .then((result) => {
                if (isEmpty(result.data)) {
                    return Promise.resolve([]);
                }
                return Promise.resolve(result.data);
            });
    }

    loadModelTrimOptions(year, make, configuration) {
        const { ITEM_DATA_LEVEL } = dataTypes;
        const datalevel =
            ITEM_DATA_LEVEL.AVAILABLE |
            ITEM_DATA_LEVEL.PRICING |
            ITEM_DATA_LEVEL.DESCRIPTIONS |
            ITEM_DATA_LEVEL.DEALER_ITEMS;
        const organizationId = ConfigsService.getOrganizationId();
        const organizationUnitId = ConfigsService.getOrganizationUnitId();
        const location = ConfigsService.getOrganizationUnitLocation();
        const locale = LocaleService.getLocale();
        const context = `datalevel=${datalevel}&context=${organizationId}.${organizationUnitId}&location=${location}&language=${locale}&userName=public&password=public`;

        return axios
            .post(`${this.basePath}/modelTrimItems?${context}`, {
                year,
                make,
                modelTrimCode: configuration.modelTrimCode,
                configurationID: configuration.id,
            })
            .then((result) => {
                return Promise.resolve(result.data.items);
            });
    }

    getQuoteVehiclePayments(vehicle, locale) {
        const organizationId = ConfigsService.getOrganizationId();
        const organizationUnitId = ConfigsService.getOrganizationUnitId();
        const location = ConfigsService.getOrganizationUnitLocation();
        const context =
            `location=${location}` +
            `&language=${locale}` +
            `&context=${organizationId}.${organizationUnitId}` +
            '&datalevel=65535&userName=public&password=public';

        const { year, make, model, modelTrimCode, configurationID, vehicleCondition, retail } = vehicle;

        const isUsed =
            vehicleCondition === VEHICLE.CONDITIONS.USED || vehicleCondition === VEHICLE.CONDITIONS.CERTIFIED;
        const paymentParameters = {
            year,
            make,
            model,
            modelTrimCode,
            configurationID,
            vehicleCondition,
            amounts: isUsed ? { retail } : null,
        };

        return new Promise((resolve) => {
            axios.post(`${this.basePath}/vehiclePayments?${context}`, paymentParameters).then((result) => {
                resolve(resetPurchaseMethodsFromOriginData(DataOrigins.VEHICLE_PAYMENTS, result.data));
            });
        });
    }

    getQuoteVehicleModelYear(vehicle, locale) {
        const { year, make, model, modelTrimCode, configurationID } = vehicle;

        const modelString = `year=${year}&make=${make}&model=${model}&modelTrimCode=${modelTrimCode}&datalevel=256`;

        const organizationId = ConfigsService.getOrganizationId();
        const organizationUnitId = ConfigsService.getOrganizationUnitId();
        const location = ConfigsService.getOrganizationUnitLocation();
        const context =
            `location=${location}` +
            `&language=${locale}` +
            `&context=${organizationId}.${organizationUnitId}` +
            `&${modelString}` +
            '&compact=false&userName=public&password=public';

        return new Promise((resolve) => {
            axios.get(`${this.basePath}/view/2.1?${context}`).then((result) => {
                let modelYear = resetModelYearFromOriginData(
                    DataOrigins.MODEL_YEAR_POWERS_TRIMS,
                    result.data.view,
                    locale
                );
                modelYear = setSelectedOptionsPackage(
                    modelYear.modelYear,
                    modelYear.modelYear.getOptionsPackageFromConfigurationId(configurationID),
                    true
                );
                resolve(modelYear.getVehicleFromConfiguration());
            });
        });
    }

    saveCreditApplication(creditApplication) {
        if (!creditApplication) {
            return Promise.reject(HTTPService.formatRejectResponse('missing parameters'));
        } else if (!AccountService.isAuthenticated()) {
            return Promise.reject(HTTPService.formatRejectResponse('not connected'));
        }
        const organizationId = ConfigsService.getOrganizationId();
        const organizationUnitId = ConfigsService.getOrganizationUnitId();
        const context = `context=${organizationId}.${organizationUnitId}`;
        const userCookie = CookieService.getToken(COOKIES.ACCOUNT.AUTHENTICATE_TOKEN);
        return new Promise((resolve) => {
            return axios
                .put(`${this.basePath}/CreditApplication?${context}`, creditApplication, {
                    headers: { 'x-jwt': userCookie },
                })
                .then((result) => {
                    resolve(result.data);
                });
        });
    }

    loadCreditApplication(creditApplicationId) {
        const organizationId = ConfigsService.getOrganizationId();
        const organizationUnitId = ConfigsService.getOrganizationUnitId();
        const context = `context=${organizationId}.${organizationUnitId}`;
        const userCookie = CookieService.getToken(COOKIES.ACCOUNT.AUTHENTICATE_TOKEN);
        const creditApplicationIdParam = creditApplicationId ? `/${creditApplicationId}` : '';

        return new Promise((resolve) => {
            return axios
                .get(`${this.basePath}/CreditApplication${creditApplicationIdParam}?${context}`, {
                    headers: { 'x-jwt': userCookie },
                })
                .then((result) => {
                    resolve(result.data);
                });
        });
    }

    saveTradeInVehicle(tradeIn) {
        if (!tradeIn) {
            return Promise.reject(HTTPService.formatRejectResponse('missing parameters'));
        } else if (!AccountService.isAuthenticated()) {
            return Promise.reject(HTTPService.formatRejectResponse('user not authenticated'));
        }

        return Promise.resolve(tradeIn);
    }

    loadTradeInVehicle(tradeInVehicleId) {
        if (!AccountService.isAuthenticated()) {
            return Promise.reject(HTTPService.formatRejectResponse('user not authenticated'));
        }
        const organizationId = ConfigsService.getOrganizationId();
        const organizationUnitId = ConfigsService.getOrganizationUnitId();
        const context = `context=${organizationId}.${organizationUnitId}`;
        const language = `locale=${LocaleService.getLocale()}`;
        const location = `location=${ConfigsService.getOrganizationUnitLocation()}`;
        const userCookie = CookieService.getToken(COOKIES.ACCOUNT.AUTHENTICATE_TOKEN);
        const tradeInVehicleIdParam = tradeInVehicleId ? `/${tradeInVehicleId}` : '';

        return new Promise((resolve) => {
            return axios
                .get(
                    `${this.basePath}/TradeInVehicle${tradeInVehicleIdParam}?${[context, language, location].join(
                        '&'
                    )}`,
                    { headers: { 'x-jwt': userCookie } }
                )
                .then((result) => {
                    resolve(result.data);
                });
        });
    }
}

export default new ShowroomService();
